import { API } from '../../API';
import {
  Dashboard,
  CreateDashboardDTO,
  UpdateDashboardDTO,
  Widget,
  CreateWidgetDTO,
  UpdateWidgetDTO,
  WidgetDataResult,
  DashboardLayoutItem,
  DashboardFilter,
  CreateFilterDTO,
  UpdateFilterDTO,
  FilterOptionsResult,
} from './types';

/** Wire format for a single draft filter override sent to the backend. */
export interface DraftFilterOverridePayload {
  filter_id: string;
  query_sql: string;
  default_value: any | null;
  config: Record<string, any>;
}

const BASE_URL = '/API/v2/custom-dashboards';

// --- snake_case <-> camelCase conversion ---

function layoutItemToSnake(item: DashboardLayoutItem): Record<string, any> {
  return {
    i: item.i,
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
    min_w: item.minW ?? null,
    min_h: item.minH ?? null,
    max_w: item.maxW ?? null,
    max_h: item.maxH ?? null,
    static: item.static ?? false,
  };
}

function layoutItemFromSnake(item: any): DashboardLayoutItem {
  const result: DashboardLayoutItem = {
    i: item.i,
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
  };
  if (item.min_w != null) result.minW = item.min_w;
  if (item.min_h != null) result.minH = item.min_h;
  if (item.max_w != null) result.maxW = item.max_w;
  if (item.max_h != null) result.maxH = item.max_h;
  if (item.static != null) result.static = item.static;
  return result;
}

function widgetFromSnake(w: any): Widget {
  return {
    id: w.id,
    dashboardId: w.dashboard_id,
    title: w.title,
    chartType: w.chart_type,
    query: {
      sql: w.query?.sql ?? '',
    },
    config: w.config ?? {},
    defaultValues: w.default_values ?? {},
    data: null,
  };
}

function filterFromSnake(f: any): DashboardFilter {
  return {
    id: f.id,
    dashboardId: f.dashboard_id,
    widgetId: f.widget_id ?? null,
    title: f.title,
    parameterName: f.parameter_name,
    filterType: f.filter_type,
    querySql: f.query_sql ?? '',
    // The dashboard GET omits filter SQL (query_sql absent) — those filters are
    // unloaded. Single-filter fetches (getFilter/create/update) include it, so
    // those come back loaded. `!= null` treats both missing and explicit null as
    // unloaded; an actual string (even '') means the SQL is known.
    querySqlLoaded: f.query_sql != null,
    defaultValue: f.default_value ?? null,
    config: f.config ?? {},
    order: f.order ?? 0,
    hidden: f.hidden ?? false,
  };
}

function dashboardFromSnake(d: any): Dashboard {
  return {
    id: d.id,
    title: d.title,
    description: d.description ?? '',
    widgets: (d.widgets ?? []).map(widgetFromSnake),
    layout: (d.layout ?? []).map(layoutItemFromSnake),
    filters: (d.filters ?? []).map(filterFromSnake),
  };
}

// --- API client ---

export interface PaginatedDashboards {
  dashboards: Dashboard[];
  total: number;
  page: number;
  size: number;
}

export const DashboardAPI = {
  async listDashboards(page: number = 1, size: number = 10): Promise<PaginatedDashboards> {
    const data = await API.fetch('GET', `${BASE_URL}?page=${page}&size=${size}`);
    return {
      dashboards: data?.objects.map(dashboardFromSnake),
      total: data?.total ?? 0,
      page: data?.page ?? page,
      size: data?.size ?? size,
    };
  },

  async getDashboard(dashboardId: string): Promise<Dashboard> {
    const data = await API.fetch('GET', `${BASE_URL}/${dashboardId}`);
    return dashboardFromSnake(data);
  },

  async createDashboard(dto: CreateDashboardDTO): Promise<Dashboard> {
    const payload: Record<string, any> = {
      title: dto.title,
      description: dto.description || null,
    };
    if (dto.filters && dto.filters.length > 0) {
      payload.filters = dto.filters.map((f) => {
        const filterPayload: Record<string, any> = {
          title: f.title,
          parameter_name: f.parameterName,
          filter_type: f.filterType,
          query_sql: f.querySql,
        };
        if (f.widgetId !== undefined) filterPayload.widget_id = f.widgetId;
        if (f.defaultValue !== undefined) filterPayload.default_value = f.defaultValue;
        if (f.config !== undefined) filterPayload.config = f.config;
        if (f.order !== undefined) filterPayload.order = f.order;
        if (f.hidden !== undefined) filterPayload.hidden = f.hidden;
        return filterPayload;
      });
    }
    const data = await API.fetch('POST', BASE_URL, null, payload);
    return dashboardFromSnake(data);
  },

  async updateDashboard(
    dashboardId: string,
    dto: UpdateDashboardDTO
  ): Promise<Dashboard> {
    const payload: Record<string, any> = {};
    if (dto.title !== undefined) payload.title = dto.title;
    if (dto.description !== undefined) payload.description = dto.description;
    if (dto.layout !== undefined) {
      payload.layout = dto.layout.map(layoutItemToSnake);
    }
    const data = await API.fetch('PATCH', `${BASE_URL}/${dashboardId}`, null, payload);
    return dashboardFromSnake(data);
  },

  async deleteDashboard(dashboardId: string): Promise<void> {
    await API.fetch('DELETE', `${BASE_URL}/${dashboardId}`);
  },

  async copyDashboard(
    sourceDashboardId: string,
    payload: { title: string; targetWorkspaceId?: number }
  ): Promise<Dashboard> {
    const data = await API.fetch(
      'POST',
      `${BASE_URL}/${sourceDashboardId}/copy`,
      null,
      {
        title: payload.title,
        target_workspace_id: payload.targetWorkspaceId ?? null,
      }
    );
    return dashboardFromSnake(data);
  },

  async addWidget(dto: CreateWidgetDTO): Promise<Widget> {
    const payload: Record<string, any> = {
      title: dto.title,
      chart_type: dto.chartType,
    };
    if (dto.query) {
      payload.query = { sql: dto.query.sql };
    }
    if (dto.config) {
      payload.config = dto.config;
    }
    if (dto.defaultValues) {
      payload.default_values = dto.defaultValues;
    }
    const data = await API.fetch(
      'POST',
      `${BASE_URL}/${dto.dashboardId}/widgets`,
      null,
      payload
    );
    return widgetFromSnake(data);
  },

  async updateWidget(
    widgetId: string,
    dashboardId: string,
    dto: UpdateWidgetDTO
  ): Promise<Widget> {
    const payload: Record<string, any> = {};
    if (dto.title !== undefined) payload.title = dto.title;
    if (dto.chartType !== undefined) payload.chart_type = dto.chartType;
    if (dto.query !== undefined) {
      payload.query = { sql: dto.query.sql };
    }
    if (dto.config !== undefined) payload.config = dto.config;
    if (dto.defaultValues !== undefined) payload.default_values = dto.defaultValues;
    const data = await API.fetch(
      'PATCH',
      `${BASE_URL}/${dashboardId}/widgets/${widgetId}`,
      null,
      payload
    );
    return widgetFromSnake(data);
  },

  async removeWidget(widgetId: string, dashboardId: string): Promise<void> {
    await API.fetch(
      'DELETE',
      `${BASE_URL}/${dashboardId}/widgets/${widgetId}`
    );
  },

  async getWidget(dashboardId: string, widgetId: string): Promise<Widget> {
    const data = await API.fetch(
      'GET',
      `${BASE_URL}/${dashboardId}/widgets/${widgetId}`
    );
    return {
      id: data.id,
      dashboardId: data.dashboard_id,
      title: data.title,
      chartType: data.chart_type,
      query: { sql: data.query_sql ?? '' },
      config: data.config ?? {},
      defaultValues: data.default_values ?? {},
      data: null,
    };
  },

  async executeRawQuery(
    dashboardId: string,
    sql: string,
    filterValues?: Record<string, any>,
    page?: number,
    pageSize?: number,
    includeHiddenValues?: boolean,
    draftFilters?: DraftFilterOverridePayload[],
  ): Promise<WidgetDataResult> {
    const payload: Record<string, any> = { sql };
    if (filterValues && Object.keys(filterValues).length > 0) {
      payload.filter_values = filterValues;
    }
    if (page != null) payload.page = page;
    if (pageSize != null) payload.page_size = pageSize;
    if (includeHiddenValues) payload.include_hidden_values = true;
    if (draftFilters && draftFilters.length > 0) payload.draft_filters = draftFilters;
    const data = await API.fetch(
      'POST',
      `${BASE_URL}/${dashboardId}/execute-query`,
      null,
      payload
    );
    return {
      columns: data.columns ?? [],
      rows: data.rows ?? [],
      executionTimeMs: data.execution_time_ms,
      error: data.error ?? null,
      totalRows: data.total_rows ?? null,
      cached: data.cached ?? false,
    };
  },

  async executeQuery(dashboardId: string, widgetId: string): Promise<WidgetDataResult> {
    const data = await API.fetch(
      'POST',
      `${BASE_URL}/${dashboardId}/widgets/${widgetId}/execute`
    );
    return {
      columns: data.columns ?? [],
      rows: data.rows ?? [],
      executionTimeMs: data.execution_time_ms,
      error: data.error ?? null,
      totalRows: data.total_rows ?? null,
      cached: data.cached ?? false,
    };
  },

  async saveLayout(
    dashboardId: string,
    layout: DashboardLayoutItem[]
  ): Promise<void> {
    await API.fetch(
      'PATCH',
      `${BASE_URL}/${dashboardId}`,
      null,
      { layout: layout.map(layoutItemToSnake) }
    );
  },

  // --- Filters ---

  async listFilters(dashboardId: string): Promise<DashboardFilter[]> {
    const data = await API.fetch('GET', `${BASE_URL}/${dashboardId}/filters`);
    return (data ?? []).map(filterFromSnake);
  },

  async getFilter(dashboardId: string, filterId: string): Promise<DashboardFilter> {
    const data = await API.fetch('GET', `${BASE_URL}/${dashboardId}/filters/${filterId}`);
    return filterFromSnake(data);
  },

  async createFilter(dashboardId: string, dto: CreateFilterDTO): Promise<DashboardFilter> {
    const payload: Record<string, any> = {
      title: dto.title,
      parameter_name: dto.parameterName,
      filter_type: dto.filterType,
      query_sql: dto.querySql,
    };
    if (dto.widgetId !== undefined) payload.widget_id = dto.widgetId;
    if (dto.defaultValue !== undefined) payload.default_value = dto.defaultValue;
    if (dto.config !== undefined) payload.config = dto.config;
    if (dto.order !== undefined) payload.order = dto.order;
    if (dto.hidden !== undefined) payload.hidden = dto.hidden;
    const data = await API.fetch('POST', `${BASE_URL}/${dashboardId}/filters`, null, payload);
    return filterFromSnake(data);
  },

  async updateFilter(
    dashboardId: string,
    filterId: string,
    dto: UpdateFilterDTO,
  ): Promise<DashboardFilter> {
    const payload: Record<string, any> = {};
    if (dto.title !== undefined) payload.title = dto.title;
    if (dto.parameterName !== undefined) payload.parameter_name = dto.parameterName;
    if (dto.filterType !== undefined) payload.filter_type = dto.filterType;
    if (dto.querySql !== undefined) payload.query_sql = dto.querySql;
    if (dto.widgetId !== undefined) payload.widget_id = dto.widgetId;
    if (dto.defaultValue !== undefined) payload.default_value = dto.defaultValue;
    if (dto.config !== undefined) payload.config = dto.config;
    if (dto.order !== undefined) payload.order = dto.order;
    if (dto.hidden !== undefined) payload.hidden = dto.hidden;
    const data = await API.fetch('PATCH', `${BASE_URL}/${dashboardId}/filters/${filterId}`, null, payload);
    return filterFromSnake(data);
  },

  async removeFilter(dashboardId: string, filterId: string): Promise<void> {
    await API.fetch('DELETE', `${BASE_URL}/${dashboardId}/filters/${filterId}`);
  },

  async getFilterOptions(
    dashboardId: string,
    filterId: string,
    filterValues?: Record<string, any>,
    includeHiddenValues?: boolean,
    draftFilters?: DraftFilterOverridePayload[],
  ): Promise<FilterOptionsResult> {
    const hasValues = filterValues && Object.keys(filterValues).length > 0;
    const hasDraftFilters = draftFilters && draftFilters.length > 0;
    let payload: Record<string, any> | undefined;
    if (hasValues || includeHiddenValues || hasDraftFilters) {
      payload = {};
      if (hasValues) payload.filter_values = filterValues;
      if (includeHiddenValues) payload.include_hidden_values = true;
      if (hasDraftFilters) payload.draft_filters = draftFilters;
    }
    const data = await API.fetch('POST', `${BASE_URL}/${dashboardId}/filters/${filterId}/options`, null, payload);
    return {
      filterId: data.filter_id,
      parameterName: data.parameter_name,
      options: data.options ?? [],
      error: data.error ?? null,
    };
  },

  async getAllFilterOptions(
    dashboardId: string,
    filterValues?: Record<string, any>,
    includeHiddenValues?: boolean,
    draftFilters?: DraftFilterOverridePayload[],
  ): Promise<FilterOptionsResult[]> {
    const hasValues = filterValues && Object.keys(filterValues).length > 0;
    const hasDraftFilters = draftFilters && draftFilters.length > 0;
    let payload: Record<string, any> | undefined;
    if (hasValues || includeHiddenValues || hasDraftFilters) {
      payload = {};
      if (hasValues) payload.filter_values = filterValues;
      if (includeHiddenValues) payload.include_hidden_values = true;
      if (hasDraftFilters) payload.draft_filters = draftFilters;
    }
    const data = await API.fetch('POST', `${BASE_URL}/${dashboardId}/filters/options`, null, payload);
    return (data ?? []).map((r: any) => ({
      filterId: r.filter_id,
      parameterName: r.parameter_name,
      options: r.options ?? [],
      error: r.error ?? null,
    }));
  },

  async executeQueryWithFilters(
    dashboardId: string,
    widgetId: string,
    filterValues: Record<string, any>,
    page?: number,
    pageSize?: number,
    includeHiddenValues?: boolean,
    draftFilters?: DraftFilterOverridePayload[],
  ): Promise<WidgetDataResult> {
    const payload: Record<string, any> = { filter_values: filterValues };
    if (page != null) payload.page = page;
    if (pageSize != null) payload.page_size = pageSize;
    if (includeHiddenValues) payload.include_hidden_values = true;
    if (draftFilters && draftFilters.length > 0) payload.draft_filters = draftFilters;
    const data = await API.fetch(
      'POST',
      `${BASE_URL}/${dashboardId}/widgets/${widgetId}/execute`,
      null,
      payload
    );
    return {
      columns: data.columns ?? [],
      rows: data.rows ?? [],
      executionTimeMs: data.execution_time_ms,
      error: data.error ?? null,
      totalRows: data.total_rows ?? null,
      cached: data.cached ?? false,
    };
  },

  async invalidateWidgetCache(dashboardId: string, widgetId: string): Promise<void> {
    await API.fetch(
      'POST',
      `${BASE_URL}/${dashboardId}/widgets/${widgetId}/invalidate-cache`
    );
  },
};

// --- read-only client bound to another base path (artifact registry proxies) ---

export type DashboardReadApi = Pick<
  typeof DashboardAPI,
  'getDashboard' | 'executeQuery' | 'executeQueryWithFilters' | 'getAllFilterOptions'
>;

type JsonFetcher = (method: 'GET' | 'POST', path: string, body?: Record<string, any>) => Promise<any>;

function widgetResultFromSnake(data: any): WidgetDataResult {
  return {
    columns: data.columns ?? [],
    rows: data.rows ?? [],
    executionTimeMs: data.execution_time_ms,
    error: data.error ?? null,
    totalRows: data.total_rows ?? null,
    cached: data.cached ?? false,
  };
}

// The viewer needs four read calls; `fetchJson` gets the path relative to the
// dashboard (''/widgets/{id}/execute//filters/options) and owns the base URL,
// headers and workspace scoping. The dashboard id arguments are ignored: the
// base path already pins the dashboard.
export function createArtifactDashboardApi(fetchJson: JsonFetcher): DashboardReadApi {
  return {
    async getDashboard() {
      return dashboardFromSnake(await fetchJson('GET', ''));
    },

    async executeQuery(_dashboardId, widgetId) {
      return widgetResultFromSnake(await fetchJson('POST', `/widgets/${widgetId}/execute`));
    },

    async executeQueryWithFilters(_dashboardId, widgetId, filterValues, page, pageSize) {
      const payload: Record<string, any> = { filter_values: filterValues };
      if (page != null) payload.page = page;
      if (pageSize != null) payload.page_size = pageSize;
      return widgetResultFromSnake(await fetchJson('POST', `/widgets/${widgetId}/execute`, payload));
    },

    async getAllFilterOptions(_dashboardId, filterValues) {
      const hasValues = filterValues && Object.keys(filterValues).length > 0;
      const data = await fetchJson('POST', '/filters/options', hasValues ? { filter_values: filterValues } : undefined);
      return (data ?? []).map((r: any) => ({
        filterId: r.filter_id,
        parameterName: r.parameter_name,
        options: r.options ?? [],
        error: r.error ?? null,
      }));
    },
  };
}
